import { useCallback, useRef, useState } from "react";

export type UseFileDropOptions = {
  onDrop: (files: File[]) => void;
  accept?: string[];
};

const DEFAULT_ACCEPT = [
  "image/*",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".txt",
  ".rtf",
  ".csv",
];

export function fileMatchesAccept(file: File, patterns: string[]): boolean {
  return patterns.some((pattern) => {
    if (pattern.endsWith("/*")) {
      return file.type.startsWith(pattern.replace("/*", "/"));
    }
    return file.name.toLowerCase().endsWith(pattern.toLowerCase());
  });
}

/**
 * Provides drag-and-drop file support for a container element.
 * Also provides a paste handler for clipboard image support.
 *
 * Usage:
 *   const { isDragOver, dropZoneProps, handlePaste } = useFileDrop({ onDrop: addFiles });
 *   <div {...dropZoneProps}> ... <TextArea onPaste={handlePaste} /> </div>
 */
export function useFileDrop(options: UseFileDropOptions) {
  const { onDrop, accept = DEFAULT_ACCEPT } = options;
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files).filter((f) =>
        fileMatchesAccept(f, accept),
      );
      if (files.length > 0) {
        onDrop(files);
      }
    },
    [onDrop, accept],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const files = items
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null && fileMatchesAccept(f, accept));

      if (files.length > 0) {
        onDrop(files);
      }
    },
    [onDrop, accept],
  );

  return {
    isDragOver,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    handlePaste,
  };
}
